import PropTypes from "prop-types";
import React, { isValidElement } from "react";
// @mui
import { styled, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { shouldForwardProp } from "@mui/system";
//
import { BUTTON } from "src/config-global";

// ----------------------------------------------------------------------

const RootStyle = styled(({ ...props }) => ( <LoadingButton {...props} /> ), {
    shouldForwardProp: (prop) => prop !== "alterIndicator" && shouldForwardProp(prop)
})(({ theme, layout, loading, alterIndicator, sx }) => ( {
    height: BUTTON.BASE_HEIGHT,
    paddingTop: BUTTON.PADDING_Y,
    paddingBottom: BUTTON.PADDING_Y,
    paddingRight: BUTTON.PADDING_X,
    paddingLeft: BUTTON.PADDING_X,
    borderRadius: BUTTON.BASE_RADIUS,

    ...( layout === "fixed" && {
        width: BUTTON.BASE_WIDTH
    } ),
    ...( layout === "adapt" && {
        width: "fit-content",
        minWidth: "100px"
    } ),
    ...( layout === "full" && {
        width: "100%"
    } ),

    "&.MuiButton-contained": {
        color: theme.palette.text.altered,
        borderColor: theme.palette.text.primary,
        backgroundColor: theme.palette.background.gray_dark,

        "&:hover": {
            borderColor: `${theme.palette.button.text_light}`,
            backgroundColor: `${theme.palette.button.text_light}`
        },

        "&.MuiButton-colorSecondary": {
            borderColor: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.main,

            "&:hover": {
                borderColor: `${theme.palette.button.purple_light}`,
                backgroundColor: `${theme.palette.button.purple_light}`
            }
        },

        "&.MuiButton-colorInfo": {
            borderColor: theme.palette.info.main,
            backgroundColor: theme.palette.info.main,

            "&:hover": {
                borderColor: `${theme.palette.button.blue_light}`,
                backgroundColor: `${theme.palette.button.blue_light}`
            }
        },

        "&.MuiButton-colorSuccess": {
            borderColor: theme.palette.success.main,
            backgroundColor: theme.palette.success.main,

            "&:hover": {
                borderColor: `${theme.palette.button.green_light}`,
                backgroundColor: `${theme.palette.button.green_light}`
            }
        },

        "&.MuiButton-colorError": {
            borderColor: theme.palette.error.main,
            backgroundColor: theme.palette.error.main,

            "&:hover": {
                borderColor: `${theme.palette.button.red_light}`,
                backgroundColor: `${theme.palette.button.red_light}`
            }
        },

        "&.Mui-disabled": {
            color: theme.palette.text.tertiary,
            borderColor: theme.palette.line,
            backgroundColor: theme.palette.line
        }
    },

    "&.MuiButton-outlined": {
        color: theme.palette.text.primary,
        borderColor: theme.palette.background.gray_dark,
        backgroundColor: theme.palette.background.default,

        "&:hover": {
            backgroundColor: theme.palette.button.gray_light
        },

        "&.MuiButton-colorSecondary": {
            color: theme.palette.secondary.main,
            borderColor: theme.palette.secondary.main,

            "&:hover": {
                color: `${theme.palette.button.purple_light}`,
                borderColor: `${theme.palette.button.purple_light}`
            }
        },

        "&.MuiButton-colorInfo": {
            color: theme.palette.info.main,
            borderColor: `${theme.palette.info.main}`,

            "&:hover": {
                color: `${theme.palette.button.blue_light}`,
                borderColor: `${theme.palette.button.blue_light}`
            }
        },

        "&.MuiButton-colorSuccess": {
            color: theme.palette.success.main,
            borderColor: theme.palette.success.main,

            "&:hover": {
                color: `${theme.palette.button.green_light}`,
                borderColor: `${theme.palette.button.green_light}`
            }
        },

        "&.MuiButton-colorError": {
            color: theme.palette.error.main,
            borderColor: theme.palette.error.main,

            "&:hover": {
                color: `${theme.palette.button.red_light}`,
                borderColor: `${theme.palette.button.red_light}`
            }
        },

        "&.Mui-disabled": {
            color: theme.palette.text.tertiary,
            borderColor: theme.palette.line,
            backgroundColor: theme.palette.background.gray
        }
    },

    "&.MuiButton-text": {
        width: "fit-content",
        height: "fit-content",
        minWidth: "unset",
        marginRight: "-4px",
        marginLeft: "-4px",
        padding: "0 4px",
        borderRadius: "4px",
        backgroundColor: "transparent",
        verticalAlign: "bottom",
        //
        "&.Mui-disabled": {
            color: theme.palette.text.tertiary,
            backgroundColor: "transparent"
        }
    },

    "&.MuiLoadingButton-root": {
        "&.MuiButton-text": {
            "&:hover": {
                backgroundColor: "transparent"
            }
        },

        "&:hover": {
            boxShadow: "none"
        }
    },

    ...( loading && {
        "&.Mui-disabled": {
            color: "transparent"
        },

        "&.MuiButton-root .MuiLoadingButton-loadingIndicator": {
            color: theme.palette.text.secondary
        },

        "&.MuiButton-colorError": {
            "&.Mui-disabled": {
                backgroundColor: theme.palette.error.dark
            },

            "&.MuiButton-root .MuiLoadingButton-loadingIndicator": {
                color: theme.palette.text.altered
            }
        },

        ...( !alterIndicator && {
            "& .MuiTypography-root": {
                color: "transparent"
            }
        } )
    } ),

    ...( loading && alterIndicator && {
        "& .MuiCircularProgress-root": {
            display: "none"
        },

        "& .MuiLoadingButton-loadingIndicator": {
            width: "100%",
            height: "100%",
            backgroundImage: `linear-gradient(to right bottom, rgba(255,255,255,0.2) 25%,
                                        transparent 25%, transparent 50%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.2) 75%,
                                        transparent 75%,transparent)`,
            backgroundSize: "60px 60px",
            animation: "loading 1.5s linear infinite",
            "@keyframes loading": {
                "0%": {
                    backgroundPosition: "0 0"
                },
                "100%": {
                    backgroundPosition: "60px 0"
                }
            }
        }
    } )
} ));

// ----------------------------------------------------------------------

const XButton = ({ variant = "contained", layout = "adapt", loading = false, alterIndicator = false, children, typographyProps, ...other }) => (
    <RootStyle variant={variant} layout={layout} loading={loading} disableRipple={variant === "text"} alterIndicator={alterIndicator} {...other}>
        {isValidElement(children) ? (
            <>{children}</>
        ) : (
            <Typography variant={variant === "contained" ? "body2" : "body1"} color="inherit" noWrap {...typographyProps}>
                {children}
            </Typography>
        )}
    </RootStyle>
);

XButton.propTypes = {
    variant: PropTypes.oneOf(["contained", "outlined", "text"]),
    layout: PropTypes.oneOf(["fixed", "adapt", "full"]),
    children: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.arrayOf(PropTypes.node)
    ]).isRequired,
    typographyProps: PropTypes.object,
    loading: PropTypes.bool,
    alterIndicator: PropTypes.bool
};

export default XButton;
